import { type FormEvent, useState } from 'react';
import type { PlatformUser } from '../../hooks/useAdminData';
import { useUpdateUserFull } from '../../hooks/useAdminData';
import { Modal } from '../Modal';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: PlatformUser | null;
}

export function EditUserModal({ open, onClose, user }: EditUserModalProps) {
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState(user?.role ?? '');
  const [platformRole, setPlatformRole] = useState(user?.platformRole ?? '');
  const [prevUserId, setPrevUserId] = useState(user?.id);

  const updateUser = useUpdateUserFull();

  // Reset form state when the user prop changes (React 19 "adjust state on prop change" pattern).
  if (user && user.id !== prevUserId) {
    setPrevUserId(user.id);
    setFullName(user.fullName);
    setEmail(user.email);
    setRole(user.role);
    setPlatformRole(user.platformRole ?? '');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;

    updateUser.mutate(
      {
        userId: user.id,
        data: {
          fullName,
          email,
          role,
          platformRole: platformRole || null,
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit User">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Full Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="sales_rep">Sales Rep</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Platform Role
          </label>
          <select
            value={platformRole}
            onChange={(e) => setPlatformRole(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="">Regular</option>
            <option value="platform_admin">Platform Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateUser.isPending}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateUser.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
